import React, { useEffect, useState } from "react";
import { Drawer, Empty, Spin, Table, Tabs, Tag, Tooltip, message } from "antd";
import type { ColumnsType } from "antd/es/table";
import { threeMReportApis } from "../../../../api/ThreeMReport";
import { headLabelOf, headOrderOf, moneyPerUnit } from "../../shared/useRateUnit";
import { fmtINR, fmtINRCompact, fmtQty, num, perUnit } from "./machineryShared";

// "What is this Equipment number made of?" — opened from the Equipment cell of
// the activity→task machinery register, at whichever scope was clicked (an
// activity row, or one task row).
//
// Three views of ONE figure, and all three must sum to it: by MACHINE (which
// assets burned the money), by NORM (which BOQ lines drove them) and by COST
// HEAD (depreciation…crew). Equipment money is live-derived — version-in-force
// × asset rates — so none of this is stored; the backend re-expands it per
// request and this drawer only formats.

const INK = "#022c57";
const MUTED = "#8c8c8c";

interface DrillAsset {
  assetId?: number;
  assetName?: string;
  uomName?: string;
  hours?: string;
  hourlyRate?: string;
  amount?: string;
  normCount?: number;
  missingRate?: boolean;
  notDeployed?: boolean;
}

interface DrillNorm {
  deploymentNormId?: number;
  normCode?: string;
  description?: string;
  quantity?: string;
  uomName?: string;
  hours?: string;
  amount?: string;
  taskCount?: number;
  noVersion?: boolean;
  missingRateCount?: number;
  assets?: DrillAsset[];
}

interface DrillData {
  scopeName?: string;
  workQuantity?: string;
  workUom?: string;
  totalHours?: string;
  totalAmount?: string;
  taskCount?: number;
  norms?: DrillNorm[];
  assets?: DrillAsset[];
  heads?: Array<{ head: string; amount: string }>;
}

export interface EquipmentDrillScope {
  /** Label for the drawer title. */
  name: string;
  activityId?: number;
  taskId?: number;
  /** Shown in the header so the drawer ties back to the cell that opened it. */
  expectedAmount?: number;
}

interface Props {
  open: boolean;
  projectId?: number | string;
  phaseId?: number;
  scope: EquipmentDrillScope | null;
  onClose: () => void;
  getContainer?: any;
}

const EquipmentDrillDrawer: React.FC<Props> = ({
  open,
  projectId,
  phaseId,
  scope,
  onClose,
  getContainer,
}) => {
  const [data, setData] = useState<DrillData | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !projectId || !scope) return;
    let cancelled = false;
    setLoading(true);
    setData(null);
    threeMReportApis
      .get3MEquipmentDrill(projectId, {
        phaseId,
        activityId: scope.activityId,
        taskId: scope.taskId,
      })
      .then((response) => {
        if (cancelled) return;
        if (response?.status) setData(response?.data || null);
        else {
          setData(null);
          message.error("Could not load the equipment breakdown");
        }
      })
      .catch((err) => {
        console.error("Error loading equipment drill:", err);
        if (!cancelled) {
          setData(null);
          message.error("Could not load the equipment breakdown");
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, projectId, phaseId, scope]);

  const qty = num(data?.workQuantity);
  const uom = data?.workUom || "";
  // Per-unit is on the SCOPE's work quantity — the same basis the register row
  // that opened this drawer uses, so the two can never disagree. Blank, never
  // zero, when there is no basis to divide by.
  const rateCell = (value: any, bold = false) => {
    const amount = num(value);
    const rate = uom && qty > 0 ? perUnit(amount, qty) : null;
    return (
      <div>
        <Tooltip title={fmtINR(amount)}>
          <span
            style={{
              fontWeight: bold ? 600 : 400,
              color: bold ? INK : undefined,
              fontVariantNumeric: "tabular-nums",
              cursor: "default",
            }}
          >
            {fmtINRCompact(amount)}
          </span>
        </Tooltip>
        {rate !== null ? (
          <div
            style={{
              fontSize: 11,
              color: "#8094ae",
              fontVariantNumeric: "tabular-nums",
              whiteSpace: "nowrap",
            }}
          >
            ₹{moneyPerUnit(rate)}/{uom}
          </div>
        ) : null}
      </div>
    );
  };

  const assetColumns: ColumnsType<DrillAsset> = [
    {
      title: "Machine",
      key: "asset",
      render: (_: any, row) => (
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
          <span style={{ fontWeight: 500 }}>
            {row.assetName || (row.assetId ? `Asset #${row.assetId}` : "—")}
          </span>
          {row.notDeployed ? (
            <Tooltip title="Considered in the deployment matrix but never deployed in this scope — contributes no cost">
              <Tag style={{ marginRight: 0, fontSize: 10 }}>not deployed</Tag>
            </Tooltip>
          ) : null}
          {row.missingRate ? (
            <Tooltip title="At least one line for this machine had no effective hourly rate in force">
              <Tag color="red" style={{ marginRight: 0, fontSize: 10 }}>
                no rate
              </Tag>
            </Tooltip>
          ) : null}
        </span>
      ),
    },
    {
      title: "Hours",
      dataIndex: "hours",
      key: "hours",
      width: 100,
      align: "right",
      render: (value: any) => (
        <span style={{ fontVariantNumeric: "tabular-nums" }}>
          {fmtQty(value)}
        </span>
      ),
    },
    {
      title: "₹/hr",
      key: "hourlyRate",
      width: 110,
      align: "right",
      // Blank when the machine was priced at more than one rate across the
      // scope (a rate version changed mid-scope): an average would be a number
      // no version ever charged.
      render: (_: any, row) =>
        row.hourlyRate ? (
          <span style={{ fontVariantNumeric: "tabular-nums" }}>
            {fmtINR(row.hourlyRate)}
          </span>
        ) : (
          <Tooltip title="This machine was priced at more than one hourly rate in this scope — a single figure would match no rate version">
            <Tag style={{ marginRight: 0 }}>mixed</Tag>
          </Tooltip>
        ),
    },
    {
      title: "Amount",
      key: "amount",
      width: 140,
      align: "right",
      render: (_: any, row) => rateCell(row.amount),
    },
  ];

  const normColumns: ColumnsType<DrillNorm> = [
    {
      title: "Equipment Norm",
      key: "norm",
      render: (_: any, row) => (
        <>
          <div style={{ fontWeight: 500 }}>
            {row.normCode || "—"}
            {row.noVersion ? (
              <Tooltip title="No matrix version in force for at least one task in this scope">
                <Tag color="orange" style={{ marginLeft: 6, fontSize: 10 }}>
                  no version
                </Tag>
              </Tooltip>
            ) : null}
          </div>
          {row.description ? (
            <div style={{ fontSize: 11, color: MUTED }}>{row.description}</div>
          ) : null}
          <div style={{ fontSize: 11, color: MUTED }}>
            {row.taskCount} {row.taskCount === 1 ? "task" : "tasks"} ·{" "}
            {(row.assets || []).length} machines
          </div>
        </>
      ),
    },
    {
      title: "Qty",
      key: "qty",
      width: 120,
      align: "right",
      render: (_: any, row) => (
        <span style={{ fontVariantNumeric: "tabular-nums" }}>
          {fmtQty(row.quantity)}
          {row.uomName ? (
            <span style={{ color: MUTED, fontSize: 11 }}> {row.uomName}</span>
          ) : null}
        </span>
      ),
    },
    {
      title: "Hours",
      dataIndex: "hours",
      key: "hours",
      width: 100,
      align: "right",
      render: (value: any) => (
        <span style={{ fontVariantNumeric: "tabular-nums" }}>
          {fmtQty(value)}
        </span>
      ),
    },
    {
      title: "Amount",
      key: "amount",
      width: 140,
      align: "right",
      render: (_: any, row) => rateCell(row.amount, true),
    },
  ];

  const headRows = (data?.heads || [])
    .slice()
    .sort((a, b) => headOrderOf(a.head) - headOrderOf(b.head))
    .map((h) => ({ key: h.head, label: headLabelOf(h.head), amount: h.amount }));

  const headColumns: ColumnsType<any> = [
    {
      title: "Cost Head",
      key: "head",
      render: (_: any, row) => (
        <Tag color="cyan" style={{ marginRight: 0 }}>
          {row.label}
        </Tag>
      ),
    },
    {
      title: "Amount",
      key: "amount",
      width: 160,
      align: "right",
      render: (_: any, row) => rateCell(row.amount),
    },
  ];

  const total = num(data?.totalAmount);

  return (
    <Drawer
      title={
        <div>
          <div style={{ fontWeight: 600, color: INK }}>
            {scope?.name || data?.scopeName || "Equipment"} — Equipment
          </div>
          <div style={{ fontSize: 12, color: MUTED, fontWeight: 400 }}>
            {scope?.taskId ? "Task" : "Activity"} scope · live-derived from the
            deployment norms in force
          </div>
        </div>
      }
      placement="right"
      width="min(880px, 100vw)"
      open={open}
      onClose={onClose}
      destroyOnClose
      getContainer={getContainer}
    >
      <Spin spinning={loading}>
        {!loading && !data ? (
          <Empty description="No equipment cost in this scope" />
        ) : (
          <>
            <div
              style={{
                display: "flex",
                gap: 24,
                flexWrap: "wrap",
                padding: "10px 14px",
                background: "#f5f7fa",
                borderRadius: 6,
                marginBottom: 14,
              }}
            >
              <div>
                <div style={{ fontSize: 11, color: MUTED }}>Equipment Cost</div>
                <div style={{ fontWeight: 600, color: INK, fontSize: 16 }}>
                  {fmtINR(total)}
                </div>
              </div>
              <div>
                <div style={{ fontSize: 11, color: MUTED }}>Machine Hours</div>
                <div style={{ fontWeight: 600, color: INK, fontSize: 16 }}>
                  {fmtQty(data?.totalHours)}
                </div>
              </div>
              <div>
                <div style={{ fontSize: 11, color: MUTED }}>Work Quantity</div>
                <div style={{ fontWeight: 600, color: INK, fontSize: 16 }}>
                  {qty > 0 ? `${fmtQty(qty)} ${uom}` : "—"}
                </div>
              </div>
              <div>
                <div style={{ fontSize: 11, color: MUTED }}>Rate</div>
                <div style={{ fontWeight: 600, color: INK, fontSize: 16 }}>
                  {qty > 0 && uom ? (
                    <>
                      ₹{moneyPerUnit(perUnit(total, qty))}
                      <span style={{ fontSize: 12, color: MUTED }}>/{uom}</span>
                    </>
                  ) : (
                    "—"
                  )}
                </div>
              </div>
              <div>
                <div style={{ fontSize: 11, color: MUTED }}>Tasks</div>
                <div style={{ fontWeight: 600, color: INK, fontSize: 16 }}>
                  {data?.taskCount ?? 0}
                </div>
              </div>
            </div>

            <Tabs
              defaultActiveKey="machines"
              items={[
                {
                  key: "machines",
                  label: `By Machine (${(data?.assets || []).length})`,
                  children: (
                    <Table<DrillAsset>
                      className="customTable"
                      size="small"
                      rowKey={(row) => `A-${row.assetId ?? 0}`}
                      columns={assetColumns}
                      dataSource={data?.assets || []}
                      pagination={false}
                      bordered
                      onRow={(row) =>
                        row.notDeployed ? { style: { opacity: 0.55 } } : {}
                      }
                      scroll={{ x: 620 }}
                    />
                  ),
                },
                {
                  key: "norms",
                  label: `By Norm (${(data?.norms || []).length})`,
                  children: (
                    <Table<DrillNorm>
                      className="customTable"
                      size="small"
                      rowKey={(row) => `N-${row.deploymentNormId ?? 0}`}
                      columns={normColumns}
                      dataSource={data?.norms || []}
                      pagination={false}
                      bordered
                      expandable={{
                        rowExpandable: (row) => (row.assets || []).length > 0,
                        expandedRowRender: (row) => (
                          <Table<DrillAsset>
                            className="customTable"
                            size="small"
                            rowKey={(a) => `N${row.deploymentNormId}-A${a.assetId ?? 0}`}
                            columns={assetColumns}
                            dataSource={row.assets || []}
                            pagination={false}
                            bordered
                            onRow={(a) =>
                              a.notDeployed ? { style: { opacity: 0.55 } } : {}
                            }
                          />
                        ),
                      }}
                      scroll={{ x: 620 }}
                    />
                  ),
                },
                {
                  key: "heads",
                  label: `Cost Heads (${headRows.length})`,
                  children: headRows.length ? (
                    <>
                      <Table
                        className="customTable"
                        size="small"
                        rowKey="key"
                        columns={headColumns}
                        dataSource={headRows}
                        pagination={false}
                        bordered
                        summary={() => (
                          <Table.Summary.Row>
                            <Table.Summary.Cell index={0}>
                              <span style={{ fontWeight: 600, color: INK }}>
                                Total
                              </span>
                            </Table.Summary.Cell>
                            <Table.Summary.Cell index={1} align="right">
                              {rateCell(total, true)}
                            </Table.Summary.Cell>
                          </Table.Summary.Row>
                        )}
                      />
                      <div
                        style={{ fontSize: 11, color: "#8094ae", marginTop: 6 }}
                      >
                        {qty > 0 && uom
                          ? `Per unit = amount ÷ work quantity (${fmtQty(qty)} ${uom}).`
                          : "No work quantity in this scope — per-unit rates unavailable."}
                      </div>
                    </>
                  ) : (
                    <Empty
                      image={Empty.PRESENTED_IMAGE_SIMPLE}
                      description="No cost-head split available for this scope"
                    />
                  ),
                },
              ]}
            />
          </>
        )}
      </Spin>
    </Drawer>
  );
};

export default EquipmentDrillDrawer;
